<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { Dropdown } from 'bootstrap'
import BcsTooltip from '../BcsTooltip.vue'

const props = defineProps({
  // Unique ID for the dropdown
  id: {
    type: String,
    required: true
  },
  // Filter label. Omitted when the parent already labels the control.
  label: {
    type: String,
    default: ''
  },
  // Currently selected value (can be single value or array for multi-select)
  modelValue: {
    type: [String, Number, Array, null],
    default: null
  },
  // Dropdown options
  options: {
    type: Array,
    default: () => []
  },
  // Text to display when no selection is made (optional)
  placeholder: {
    type: String,
    default: 'Select'
  },
  // Optional prefix to display before the selected single-select label
  selectedValuePrefix: {
    type: String,
    default: ''
  },
  // If the dropdown has a default selection (to show with purple background)
  hasDefaultSelection: {
    type: Boolean,
    default: false
  },
  // Custom width (optional)
  width: {
    type: String,
    default: 'w-100'
  },
  // Enable multi-select mode
  multiSelect: {
    type: Boolean,
    default: false
  },
  // Tooltip text (optional) - shows info icon with tooltip when not empty
  tooltipText: {
    type: String,
    default: ''
  },
  // Grouped options: Array of { label: string, options: Array, tooltip?: string }
  groupedOptions: {
    type: Array,
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

// Refs for programmatic control
const toggleRef = ref(null)
const menuRef = ref(null)
let dropdownInstance = null

// Programmatic outside-click close safety net (in addition to data-bs-auto-close)
const onDocClick = (e) => {
  const toggleEl = toggleRef.value
  const menuEl = menuRef.value
  if (!toggleEl) return
  const target = e.target
  // If click is inside toggle or menu, ignore
  if (toggleEl.contains(target) || (menuEl && menuEl.contains(target))) return
  if (dropdownInstance) dropdownInstance.hide()
}

const onShown = () => {
  // Attach listener when dropdown opens (only for multi-select)
  if (props.multiSelect) {
    document.addEventListener('click', onDocClick, true)
  }
}

const onHidden = () => {
  // Detach listener when dropdown closes
  document.removeEventListener('click', onDocClick, true)
}

onMounted(() => {
  if (toggleRef.value) {
    // Ensure Bootstrap instance respects multiSelect autoclose behavior
    dropdownInstance = Dropdown.getOrCreateInstance(toggleRef.value, {
      autoClose: props.multiSelect ? 'outside' : true
    })
    toggleRef.value.addEventListener('shown.bs.dropdown', onShown)
    toggleRef.value.addEventListener('hidden.bs.dropdown', onHidden)
  }
})

onBeforeUnmount(() => {
  if (toggleRef.value) {
    toggleRef.value.removeEventListener('shown.bs.dropdown', onShown)
    toggleRef.value.removeEventListener('hidden.bs.dropdown', onHidden)
  }
  document.removeEventListener('click', onDocClick, true)
})

const close = () => {
  if (dropdownInstance) {
    dropdownInstance.hide()
  }
}

// Calculate the text to display based on the selected value
const displayText = computed(() => {
  if (props.multiSelect) {
    // Multi-select mode
    if (!props.modelValue || props.modelValue.length === 0) {
      return props.placeholder
    }
    if (props.modelValue.length === 1) {
      const selectedOption = allOptions.value.find(o => o.value === props.modelValue[0])
      return selectedOption ? selectedOption.label : '1 Selected'
    }
    return `${props.modelValue.length} Selected`
  } else {
    // Single select mode
    if (!props.modelValue) return props.placeholder
    const selectedOption = allOptions.value.find(o => o.value === props.modelValue)
    if (!selectedOption) {
      return props.placeholder
    }
    return props.selectedValuePrefix
      ? `${props.selectedValuePrefix}: ${selectedOption.label}`
      : selectedOption.label
  }
})

// Determine if the dropdown should be displayed as selected
const isSelected = computed(() => {
  if (props.multiSelect) {
    return props.modelValue && props.modelValue.length > 0
  }
  return props.hasDefaultSelection || props.modelValue !== null
})

// Button class based on state
const buttonClass = computed(() => {
  return isSelected.value ? 'btn-filter-selected' : 'btn-filter-default'
})

// Check if an option is selected (for multi-select)
const isOptionSelected = (value) => {
  if (!props.multiSelect) {
    return props.modelValue === value
  }
  return props.modelValue && props.modelValue.includes(value)
}

// Compute flattened options for display text calculation when using grouped options
const allOptions = computed(() => {
  if (props.groupedOptions && props.groupedOptions.length > 0) {
    return props.groupedOptions.flatMap(group => group.options || [])
  }
  return props.options
})

// Check if we have grouped options
const hasGroupedOptions = computed(() => {
  return props.groupedOptions && props.groupedOptions.length > 0
})

const isOptionDisabled = (option) => {
  return Boolean(option?.disabled)
}

// Handle option selection
const handleSelect = (option) => {
  if (isOptionDisabled(option)) {
    return
  }

  const value = option.value

  if (props.multiSelect) {
    // Toggle selection
    const currentValue = props.modelValue || []
    const index = currentValue.indexOf(value)

    let newValue
    if (index > -1) {
      // Remove from selection
      newValue = currentValue.filter(v => v !== value)
    } else {
      // Add to selection
      newValue = [...currentValue, value]
    }

    emit('update:modelValue', newValue)
    emit('change', newValue)
  } else {
    // Single select mode
    emit('update:modelValue', value)
    emit('change', value)
    // Ensure the dropdown closes immediately on selection
    if (dropdownInstance) {
      dropdownInstance.hide()
    }
  }
}

defineExpose({ close })
</script>

<template>
  <div>
    <label v-if="label" :for="id" class="text-14 fw-bold mb-1.5 d-flex align-items-center gap-1">
      {{ label }}
      <BcsTooltip v-if="tooltipText" :simpleText="tooltipText" :show-arrow="true" :contentClass="'simple-text-tooltip'">
        <i class="bi bi-exclamation-circle text-primary"></i>
      </BcsTooltip>
    </label>
    <div class="dropdown">
      <button
        type="button"
        :class="[buttonClass, width]"
        class="btn py-2"
        :id="id"
        ref="toggleRef"
        data-bs-toggle="dropdown"
        :data-bs-auto-close="multiSelect ? 'outside' : 'true'"
        aria-expanded="false"
      >
        <span class="header-20 me-2 text-truncate"><med>{{ displayText }}</med></span>
        <i class="bi bi-caret-down-fill ms-auto"></i>
      </button>
      <ul class="dropdown-menu" :aria-labelledby="id" ref="menuRef">
        <!-- Grouped options mode -->
        <template v-if="hasGroupedOptions">
          <template v-for="(group, groupIndex) in groupedOptions" :key="groupIndex">
            <!-- Group header -->
            <li class="dropdown-group-header">
              <span class="text-14 text-gray-600">{{ group.label }}</span>
              <BcsTooltip v-if="group.tooltip" :simpleText="group.tooltip" :show-arrow="true" :contentClass="'simple-text-tooltip'">
                <i class="bi bi-exclamation-circle text-muted ms-1"></i>
              </BcsTooltip>
            </li>
            <!-- Group options -->
            <li
              v-for="option in group.options"
              :key="option.value"
              class="dropdown-item hand-cursor"
              :class="{ 'selected': isOptionSelected(option.value), 'disabled': isOptionDisabled(option) }"
              :aria-disabled="isOptionDisabled(option)"
              @click="handleSelect(option)"
            >
              <!-- Multi-select: Show checkbox -->
              <div v-if="multiSelect" class="d-flex align-items-center">
                <input
                  type="checkbox"
                  class="form-check-input me-2"
                  :checked="isOptionSelected(option.value)"
                  :disabled="isOptionDisabled(option)"
                />
                <span class="option-label">{{ option.label }}</span>
              </div>
              <!-- Single select: Just show text -->
              <span v-else class="option-label">{{ option.label }}</span>
            </li>
            <!-- Separator between groups (except after last group) -->
            <li v-if="groupIndex < groupedOptions.length - 1" class="dropdown-divider"></li>
          </template>
        </template>
        <!-- Standard options mode -->
        <template v-else>
          <li
            v-for="option in options"
            :key="option.value"
            class="dropdown-item hand-cursor"
            :class="{ 'selected': isOptionSelected(option.value), 'disabled': isOptionDisabled(option) }"
            :aria-disabled="isOptionDisabled(option)"
            @click="handleSelect(option)"
          >
            <!-- Multi-select: Show checkbox -->
            <div v-if="multiSelect" class="d-flex align-items-center">
              <input
                type="checkbox"
                class="form-check-input me-2"
                :checked="isOptionSelected(option.value)"
                :disabled="isOptionDisabled(option)"
              />
              <span class="option-label text-16">{{ option.label }}</span>
            </div>
            <!-- Single select: Just show text -->
            <span v-else class="option-label text-16">{{ option.label }}</span>
          </li>
        </template>
      </ul>
    </div>
  </div>
</template>

<style scoped lang="scss">
// Dropdown buttons - Default state (no selection)
.btn-filter-default {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.375rem 0.75rem;
  background-color: #fff;
  border: 1px solid var(--bs-primary);
  border-radius: 0.375rem;
  color: var(--bs-primary);
  text-align: left;
  
  &:hover {
    background-color: #fff;
    border-color: var(--bs-primary);
    color: var(--bs-primary);
  }
  
  &:focus,
  &:active,
  &.show {
    background-color: #fff !important;
    border-color: var(--bs-primary) !important;
    color: var(--bs-primary) !important;
    box-shadow: 0 0 0 0.2rem rgba(var(--bs-primary-rgb), 0.25) !important;
  }
  
  .bi-caret-down-fill {
    color: var(--bs-primary);
  }
}

// Dropdown buttons - Selected state
.btn-filter-selected {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.375rem 0.75rem;
  background-color: var(--bs-primary);
  border: 1px solid var(--bs-primary);
  border-radius: 0.375rem;
  color: #fff;
  text-align: left;
  
  &:hover {
    background-color: var(--bs-primary);
    border-color: var(--bs-primary);
    color: #fff;
  }
  
  &:focus,
  &:active,
  &.show {
    background-color: var(--bs-primary) !important;
    border-color: var(--bs-primary) !important;
    color: #fff !important;
    box-shadow: 0 0 0 0.2rem rgba(var(--bs-primary-rgb), 0.25) !important;
  }
  
  .bi-caret-down-fill {
    color: #fff;
  }
}

// Dropdown menu items
.dropdown-menu {
  border-radius: 0.375rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  max-height: 324px; // Maximum height for the dropdown
  overflow-y: auto; // Enable internal scroll
  width: auto !important; // Allow width to adapt to content
  min-width: 100%; // Minimum width matches button width
  
  .dropdown-item {
    box-sizing: border-box;
    min-height: 24px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 4px 16px;
    cursor: pointer;
    white-space: nowrap;

    &.disabled {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;

      .option-label {
        color: var(--bs-gray-600);
      }
    }
    
    &:hover {
      background-color: var(--gray-200);
    }
    
    // Selected state (single AND multi select): Eggplant primary background
    &.selected {
      background-color: var(--bs-primary);

      .option-label {
        color: #fff;
      }

      &:hover {
        background-color: var(--bs-primary);
      }

    }
    
    .option-label {
      font-family: 'DM Sans', sans-serif;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%; // 24px
      color: var(--bs-gray-900, #212529);
    }
    
    // Checkbox styling for multi-select
    .form-check-input {
      cursor: pointer;
      // Matches the 6px the checked halo below paints outside the box (4px outline +
      // 2px offset), so the halo stops at the content-box edge and the item's 4px
      // vertical padding stays visible instead of being overlapped.
      margin: 6px 0;
      width: 16px;
      height: 16px;
      // Unchecked: Gray 700 border + 4px radius.
      border: 1px solid var(--bs-gray-700);
      border-radius: 4px;
      // Enlarge the check glyph a bit within the box.
      background-size: 90% 90%;

      // 4px outer halo, offset 2px outside the box, kept while checked.
      // outline + outline-offset paint outside the element so the checkbox
      // size/layout stay unchanged.
      &:checked {
        background-color: var(--bs-primary);
        border: none;
        outline: 4px solid #6b36dc;
        outline-offset: 2px;
        box-shadow: none;
      }
    }
  }
  
  // Group header styling
  .dropdown-group-header {
    padding: 0.3rem 0.9rem 0.4rem;
    display: flex;
    align-items: center;
    
    .bi-exclamation-circle {
      font-size: 0.7rem;
      cursor: help;
    }
  }
  
  // Divider between groups
  .dropdown-divider {
    opacity: 0.25;
    margin: 0.6rem 0;
    border-top: 1px solid var(--bs-secondary);
  }
}

</style>
